import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FormProvider, useForm } from 'react-hook-form'
import { SimpleModal } from '@sellpy/design-system-react-web'
import { money } from '@sellpy/commons'
import { useDispatch } from 'react-redux'
import styled from 'styled-components'

import { Spinner } from '../../components/spinners/Spinners'
import useUserTotalBalance from '../../balance/hooks/useUserTotalBalance.js'
import { requestCreditPayout } from '../../../common/payout/actions.js'
import { showToast } from '../../uiComponents/notifications/toastState.js'
import DAC7 from '../../balance/components/DAC7.jsx'
import { commonPayoutErrors } from '../utils.js'
import { useDac7BlocksForUser } from '../../profile/dac7/hooks/useDac7BlocksForUser.js'
import {
  analyticsAutomaticPayoutSettings,
  analyticsCreditConversion,
  analyticsOptInAutomaticTransfers
} from '../../../common/analytics/actions.js'
import useAnalyticsItems from '../../hooks/useAnalyticsItems.js'
import { CHECKOUT_TYPES } from '../../checkout/analytics.js'
import {
  evictAutomaticPayoutConfigCache,
  evictPayoutsCache,
  evictUserBalanceCache
} from '../../item/tools.js'
import OptInAutomaticPayouts from '../components/OptInAutomaticPayouts.jsx'
import CreditPayout from './CreditPayout.jsx'
import CreditPayoutComplete from './CreditPayoutComplete.jsx'

const StyledSimpleModal = styled(SimpleModal)`
  > [role='dialog'] {
    max-width: 600px;
  }
`

const CreditPayoutModal = ({ isOpen, setIsOpen, onSubmitCallback, checkoutType }) => {
  const { t } = useTranslation(['balance', 'common'])
  const [creditPayoutComplete, setCreditPayoutComplete] = useState(false)
  const [loading, setLoading] = useState(false)
  const [balance, refetch] = useUserTotalBalance()
  const methods = useForm({
    mode: 'onChange'
  })
  const dispatch = useDispatch()
  const analyticsItems = useAnalyticsItems()
  const { dac7PayoutBlock } = useDac7BlocksForUser()

  const {
    handleSubmit,
    formState: { isValid, isSubmitting },
    reset
  } = methods

  const onSubmit = async (data) => {
    const updateAutomaticPayoutMethod = data.updateAutomaticPayouts === 'true' ? true : false
    try {
      setLoading(true)
      const { amount, currency } = money.toSubUnit({
        amount: data.amount,
        currency: balance.currency
      })

      const creditPayout = await dispatch(
        requestCreditPayout({
          amount,
          currency,
          region: process.env.REGION,
          updateAutomaticPayoutMethod
        })
      )
      evictUserBalanceCache(currency)
      evictPayoutsCache()

      if (updateAutomaticPayoutMethod) {
        evictAutomaticPayoutConfigCache()
        analyticsAutomaticPayoutSettings({
          payoutType: 'bank',
          clickSource: window.location.pathname,
          settingAction: 'signup'
        })
      }
      const analyticsPayload = () => {
        if (!checkoutType) return {}
        if (checkoutType === CHECKOUT_TYPES.CART) {
          return analyticsItems.length
            ? {
                checkoutType,
                items: analyticsItems,
                totalCreditsAvailable: balance?.amount,
                creditsConverted: amount
              }
            : {}
        }
        return { checkoutType }
      }
      analyticsCreditConversion(analyticsPayload())

      analyticsOptInAutomaticTransfers({
        payoutType: 'credit',
        updateAutomaticPayoutMethod: data.updateAutomaticPayouts,
        amount,
        clickSource: window.location.pathname
      })

      setCreditPayoutComplete(creditPayout)
    } catch (error) {
      showToast(commonPayoutErrors(error, t))
    } finally {
      setLoading(false)
      refetch()
      onSubmitCallback && onSubmitCallback()
      reset()
    }
  }

  const onClose = () => {
    setCreditPayoutComplete(false)
    setIsOpen(false)
  }

  let modalContent = loading ? <Spinner /> : <CreditPayout balance={balance} />
  modalContent = creditPayoutComplete ? (
    <CreditPayoutComplete creditPayout={creditPayoutComplete} />
  ) : (
    modalContent
  )

  const modalCTA =
    creditPayoutComplete || loading
      ? undefined
      : [
          {
            color: 'black',
            size: 'medium',
            variant: 'filled',
            label: t('credit.payout.accept'),
            disabled: !isValid || isSubmitting,
            loading: isSubmitting,
            type: 'submit'
          }
        ]

  return (
    <>
      {dac7PayoutBlock ? (
        <DAC7 openModalInfo={isOpen} setOpenModalInfo={onClose} showInfoBox={false} />
      ) : (
        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StyledSimpleModal
              headline={t('credit.payout.headline')}
              isOpen={isOpen}
              bottomCTA={modalCTA}
              onClose={onClose}
            >
              {modalContent}
              {loading || creditPayoutComplete ? null : (
                <OptInAutomaticPayouts labelKey={'optIn.credits.title'} />
              )}
            </StyledSimpleModal>
          </form>
        </FormProvider>
      )}
    </>
  )
}

export default CreditPayoutModal
